// Destination kits (docs/ORGANISER_PLAN.md §2): reusable content a trip is built on.
// A language kit (lang-ja: decks for scripts, everyday words, numbers, sound effects) and a
// region kit (region-hokkaido: places, climate, theme, islands, region decks) that names its
// language. A trip seed (packs/<id>/) names its region kit; the build assembles the three
// into the one Pack the app loads, and the family's edits are events on top of it.
import { z } from 'zod'
import { RewardMachineId } from '../rewards'
import { Climate, Deck, Place, Theme } from './schema'

const id = z.string().regex(/^[a-z0-9][a-z0-9-]*$/, 'kebab-case id')
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')

export const LanguageKit = z.object({
  id,
  version: z.number().int().positive(),
  title: z.string().min(1),
  /** BCP 47 locale for speech (ja-JP, ko-KR, it-IT). */
  locale: z.string().min(2),
  /** Script tracks this language has (none for Italian). */
  scripts: z.array(z.string()),
  /** Its script's name in the reading machine's title ("Kana Capsules", "Hangul Capsules"). */
  readName: z.string().min(1).optional(),
})
export type LanguageKit = z.infer<typeof LanguageKit>

export const RegionKit = z.object({
  id,
  version: z.number().int().positive(),
  title: z.string().min(1),
  language: id,
  season: z.enum(['winter', 'spring', 'summer', 'autumn', 'any']),
  /**
   * The places (src/map/places/registry ids) this region's stages may use, first = a new trip's
   * starter stage. A list, not a limit: any Japan place can be a stage island (the stage editor
   * offers them all), and the Map treats every place the same.
   */
  islands: z.array(id),
  climatology: z.record(z.string(), z.record(z.string().regex(/^(0[1-9]|1[0-2])$/), Climate)),
  daylight: z.object({ sunrise: z.string().regex(/^\d{2}:\d{2}$/), sunset: z.string().regex(/^\d{2}:\d{2}$/) }),
  /** The region's time zone, offered when a trip is made in the app (IANA, e.g. Asia/Tokyo). */
  timezone: z.string().min(1).optional(),
  /** The reward machine for this destination (B16); none = the neutral one. */
  reward: z.object({ machine: RewardMachineId }).optional(),
  /** Local money for Number Rush and prices (ISO 4217, e.g. JPY). */
  currency: z.string().regex(/^[A-Z]{3}$/).optional(),
  /** Local dev and e2e only: scripts/build-pack.ts leaves it out of a deploy build (`--dev` adds it). */
  devOnly: z.boolean().optional(),
})
export type RegionKit = z.infer<typeof RegionKit>

/**
 * A region kit with its language kit, as served at runtime (D3): public/packs/kits/<id>.json.
 * A trip made in the app (D2) is composed on the phone from this plus its own dates.
 */
export const KitBundle = z.object({
  id,
  version: z.number().int().positive(),
  title: z.string().min(1),
  season: RegionKit.shape.season,
  islands: z.array(id),
  timezone: z.string().optional(),
  reward: RegionKit.shape.reward,
  currency: RegionKit.shape.currency,
  language: LanguageKit,
  climatology: RegionKit.shape.climatology,
  daylight: RegionKit.shape.daylight,
  theme: Theme,
  places: z.array(Place),
  decks: z.array(Deck),
})
export type KitBundle = z.infer<typeof KitBundle>

/** One line per kit in public/packs/kits/index.json, for the new-trip picker (o12). */
export interface KitSummary {
  id: string
  title: string
  season: string
  language: string
  islands: number
  places: number
  decks: { id: string; title: string }[]
  timezone?: string
}

/** A trip made in the app (D2): a kit, a name and dates. */
export const NewTrip = z.object({
  title: z.string().trim().min(1).max(40),
  kit: id,
  startDate: isoDate,
  endDate: isoDate,
  timezone: z.string().min(1).max(64),
})
  .refine((t) => t.endDate >= t.startDate, { message: 'ends before it starts', path: ['endDate'] })
  // Every phone's trip clock reads it, so it has to be a real IANA zone.
  .refine((t) => isTimeZone(t.timezone), { message: 'unknown time zone', path: ['timezone'] })

function isTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz })
    return true
  } catch {
    return false
  }
}
export type NewTrip = z.infer<typeof NewTrip>

/** packs/<id>/trip.json: the family's trip, before any edits made in the app. */
export const TripSeedMeta = z.object({
  id,
  version: z.number().int().positive(),
  title: z.string().min(1),
  subtitle: z.string().optional(),
  timezone: z.string().min(1),
  startDate: isoDate,
  endDate: isoDate,
  kit: id,
  /** A test trip for local dev and e2e only (packs/japan-grand-tour): never in a deploy build. */
  devOnly: z.boolean().optional(),
})
export type TripSeedMeta = z.infer<typeof TripSeedMeta>
